import PropTypes from 'prop-types';
import { PUNCTUATION } from '../../../utils/constants/notation';
import {
  findOwnershipBasis,
  formatOwnershipPct,
  getListingStatusName,
  getMatchedStructures,
  getStructureName,
  sortStructures
} from '../../../utils/ownershipCriteria';

const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3 8.5l3 3 7-7"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BasisFigures = ({ figures, basis, matched }) => {
  const structures = sortStructures(figures.structures);
  const noun = findOwnershipBasis(basis).noun;
  const count = Number(figures.holderCount) || 0;
  const largestPct = formatOwnershipPct(figures.largest?.pct);
  // The ETL leaves the name empty for a holder without a name or a live profile; the stake
  // still decides the structure, so it is shown either way.
  const largestName = figures.largest?.name || (largestPct ? 'Name not available' : '');

  return (
    <>
      {!!structures.length && (
        <span className="ownership-structures">
          {structures.map((key) => {
            const isMatched = matched.includes(key);
            return (
              <span key={key} className={`ownership-structure-tag${isMatched ? ' matched' : ''}`}>
                {isMatched && <CheckIcon />}
                {getStructureName(key)}
                {isMatched && <span className="ownership-sr-only"> (matches your search)</span>}
              </span>
            );
          })}
        </span>
      )}
      <span className="ownership-holder-count">
        {count} {count === 1 ? noun : `${noun}s`}
      </span>
      {!!largestName && (
        <span className="ownership-largest">
          {!!largestPct && <span className="ownership-largest-pct">{largestPct}</span>}{' '}
          <span className="ownership-largest-name">{largestName}</span>
          {!!figures.largest?.via && (
            <span className="ownership-largest-via">via {figures.largest.via}</span>
          )}
        </span>
      )}
    </>
  );
};

BasisFigures.propTypes = {
  figures: PropTypes.object.isRequired,
  basis: PropTypes.string.isRequired,
  matched: PropTypes.arrayOf(PropTypes.string).isRequired
};

/**
 * One company's ownership on one basis, for the results list, the saved-project grid
 * and the company card: the structure labels (a check on the preset the search matched), the
 * number of shareholders, the largest stake with its holder, and the listing status. `summary`
 * is the backend's `ownershipSummary`; without it the cell renders the empty value.
 */
const OwnershipSummaryCell = ({ summary, basis, searchedBasis = null, searchedStructures }) => {
  const figures = summary?.[basis];
  const listing = getListingStatusName(summary?.listingStatus);
  if (!figures && !listing) {
    return <span className="ownership-summary-cell empty">{PUNCTUATION.EMPTY_VALUE}</span>;
  }

  const matched =
    basis === searchedBasis ? getMatchedStructures(summary, basis, searchedStructures) : [];

  return (
    <span className="ownership-summary-cell">
      {figures ? (
        <BasisFigures figures={figures} basis={basis} matched={matched} />
      ) : (
        <span className="ownership-holder-count">{PUNCTUATION.EMPTY_VALUE}</span>
      )}
      {!!listing && <span className="ownership-listing-tag">{listing}</span>}
    </span>
  );
};

OwnershipSummaryCell.propTypes = {
  summary: PropTypes.object,
  basis: PropTypes.string.isRequired,
  searchedBasis: PropTypes.string,
  searchedStructures: PropTypes.arrayOf(PropTypes.string)
};

export default OwnershipSummaryCell;
